import { medtechApiClient } from './axios';
import {
  TEMPLATE_STATUS,
  Template,
  TemplateDetail,
  TemplateRequestBody,
  TemplateVersions,
  ValidUpgradableTemplateStatus,
} from '../types';
import { convertStringToDate } from '../utils/dateUtils';
import { sortSections } from '../utils/sectionUtils';
import { Signature } from '../types/signature';

function conformDates(rawTemplate: any): any {
  rawTemplate['created_time'] = convertStringToDate(rawTemplate['created_time']);
  rawTemplate['modified_time'] = convertStringToDate(rawTemplate['modified_time']);
  return rawTemplate;
}

function resolveStatuses(rawStatuses: string[]): string[] {
  const statuses = [...rawStatuses]; // making copy to let the original list in intact
  const index = statuses.indexOf('allOpenStatuses');
  if (index === -1) {
    return statuses;
  }

  statuses.splice(index, 1); // removes allOpenStatuses

  if (statuses.indexOf(TEMPLATE_STATUS.effective) === -1) {
    statuses.push(TEMPLATE_STATUS.effective); // adds effective if not already exists
  }

  if (statuses.indexOf(TEMPLATE_STATUS.draft) === -1) {
    statuses.push(TEMPLATE_STATUS.draft); // adds draft if not already exists
  }

  return statuses;
}

const templateApi = {
  async getTemplatesByCompanyIdAndStatuses(companyId: number, statuses: string[]): Promise<Template[]> {
    const res = await medtechApiClient.get(`/${companyId}/document_templates`, {
      params: { statuses: resolveStatuses(statuses) },
      paramsSerializer: {
        indexes: null, // to remove the [] on list type params
      },
    });
    const rawTemplates = res.data;
    for (const template of rawTemplates) {
      conformDates(template);
    }
    return rawTemplates as Template[];
  },

  async getTemplateByCompanyIdAndId(companyId: number, templateId: string | undefined): Promise<TemplateDetail> {
    const endpoint_url = `/${companyId}/document_templates/${templateId}`;
    const res = await medtechApiClient.get(endpoint_url);
    const template = conformDates(res.data) as TemplateDetail;
    sortSections(template.sections ?? []);
    return template;
  },

  async createTemplate(companyId: number, newTemplate: TemplateRequestBody): Promise<TemplateDetail> {
    const postUrl = `/${companyId}/document_templates`;
    const resOrError = await medtechApiClient.post(postUrl, newTemplate);
    if (resOrError.data) {
      const template = conformDates(resOrError.data) as TemplateDetail;
      sortSections(template.sections);
      return template;
    }

    throw resOrError;
  },

  async makeTemplateEffective(companyId: number, templateUuid: string, signaturePayload: Signature): Promise<void> {
    const postUrl = `/companies/${companyId}/document_templates/${templateUuid}/effective`;
    const resOrError = await medtechApiClient.post(postUrl, signaturePayload);
    if (resOrError.status === 200) {
      return;
    }

    throw resOrError;
  },

  async createTemplateDraft(companyId: number, templateUuid: string): Promise<TemplateDetail> {
    const postUrl = `/companies/${companyId}/document_templates/${templateUuid}/draft`;
    const resOrError = await medtechApiClient.post(postUrl);
    if (resOrError.data) {
      const rawTemplate = resOrError.data;
      const template = conformDates(rawTemplate) as TemplateDetail;
      sortSections(template.sections);
      return template;
    }

    throw resOrError;
  },

  async deleteTemplateDraft(companyId: number, templateUuid: string): Promise<void> {
    const url = `/companies/${companyId}/document_templates/${templateUuid}/draft`;
    const resOrError = await medtechApiClient.delete(url);
    if (resOrError.status !== 204) {
      throw resOrError;
    }
  },

  async createNewTemplate(
    companyId: number,
    prefix: string,
    name: string,
    tagIds: number[] = [],
  ): Promise<TemplateDetail> {
    const postUrl = `/companies/${companyId}/document_templates`;
    const resOrError = await medtechApiClient.post(postUrl, {
      prefix: prefix,
      name: name,
      tag_ids: tagIds,
    });
    if (resOrError.data) {
      const rawTemplate = resOrError.data;
      return conformDates(rawTemplate) as TemplateDetail;
    }

    throw resOrError;
  },

  async updateTemplate(companyId: number, templateId: string, template: TemplateRequestBody): Promise<TemplateDetail> {
    const putUrl = `/${companyId}/document_templates/${templateId}`;
    const resOrError = await medtechApiClient.put(putUrl, template);
    if (resOrError.data) {
      const createdTemplate = conformDates(resOrError.data) as TemplateDetail;
      sortSections(createdTemplate.sections);
      return createdTemplate;
    }

    throw resOrError;
  },

  async updateTemplateDraft(
    companyId: number,
    templateUuid: string,
    template: TemplateRequestBody,
  ): Promise<TemplateDetail> {
    const putUrl = `/companies/${companyId}/document_templates/${templateUuid}/draft`;
    const resOrError = await medtechApiClient.put(putUrl, template);
    if (resOrError.data) {
      const rawTemplate = resOrError.data;
      const updatedTemplate = conformDates(rawTemplate) as TemplateDetail;
      if (updatedTemplate.sections) {
        sortSections(updatedTemplate.sections);
      }
      return updatedTemplate;
    }

    throw resOrError;
  },

  archiveRestoreTemplate(companyId: number, templateId: string, archived: boolean) {
    const requestBody = { archived: archived };
    const patchUrl = `/${companyId}/document_templates/${templateId}`;
    return medtechApiClient.patch(patchUrl, requestBody);
  },

  updateTemplateStatus(companyId: number, templateUUID: string, status: ValidUpgradableTemplateStatus) {
    const requestBody = { status: status };
    const patchUrl = `companies/${companyId}/document_templates/${templateUUID}`;
    return medtechApiClient.patch(patchUrl, requestBody);
  },

  changeTemplateOwner(companyId: number, templateUUID: string, userId: number) {
    const requestBody = { user_id: userId };
    const patchUrl = `companies/${companyId}/document_templates/${templateUUID}/draft`;
    return medtechApiClient.patch(patchUrl, requestBody);
  },

  async getTemplateVersions(companyId: number, templateUuid: string): Promise<TemplateVersions> {
    const getUrl = `/companies/${companyId}/document_templates/${templateUuid}/versions`;
    const resOrError = await medtechApiClient.get(getUrl);
    if (resOrError.status === 200) {
      return resOrError.data as TemplateVersions;
    }

    throw resOrError;
  },
};

export default templateApi;
